import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';

@Component({
  selector: 'app-custom-autocomplete',
  templateUrl: './custom-autocomplete.component.html',
  styleUrls: ['./custom-autocomplete.component.scss'],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(/* istanbul ignore next */() => CustomAutocompleteComponent),
      multi: true
    }
  ],
  imports: [AutoCompleteModule, ButtonModule, CommonModule, FormsModule, InputGroupAddonModule, ReactiveFormsModule, TableModule, TooltipModule]
})
export class CustomAutocompleteComponent implements ControlValueAccessor, OnInit {

  constructor(private elementRef: ElementRef) { }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (value: any) => { };
  onTouched = () => { };
  disabled: boolean = false;
  filteredList: CustomListDropdown[] = [];
  internalValue: any = null;
  selectedValue: CustomListDropdown | null = null;
  overlayWidth: string = '';

  @Input() class: string = "";
  //Deshabilita solo la lista (no se puede escribir ni desplegar); el botón extra y el ícono para limpiar siguen activos:
  @Input() disabledList: boolean = false;
  @Input() formControlName: string | number | null = null;
  @Input() iconExtraButton: string = "";
  @Input() labelText: string = "";
  @Input() list: CustomListDropdown[] = [];
  @Input() recovery: boolean = false;
  @Input() required: boolean = false;
  @Input() showExtraButton: boolean = false;
  @Input() titleExtraButton: string = "";
  @Output() onChangeSelect = new EventEmitter<number>();
  @Output() clickExtraButton = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges) {
    // Cuando la lista llegue o cambie, recalcula el selectedValue
    if (changes['list']) {
      this.updateSelectedValue();
      this.calculateOverlayWidth();
    }
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.calculateOverlayWidth);
    }
  }

  changeSelected(event: any) {
    const newValue = event?.value?.value || null;
    if (this.internalValue !== newValue) {
      this.internalValue = newValue;
      this.selectedValue = this.list.find(item => item.value === newValue) || null;
      this.onChange(this.internalValue);
      this.onTouched();
      this.onChangeSelect.emit(this.internalValue);
    } else {
      // Forzar actualización para evitar que muestre el valor en lugar del label
      setTimeout(() => {
        this.selectedValue = this.list.find(item => item.value === newValue) || null;
      });
    }
  }

  calculateOverlayWidth() {
    if (!this.list?.length) {
      this.overlayWidth = '';
      return;
    }
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const computed = getComputedStyle(document.body);
    const fontString = `${computed.fontStyle} ${computed.fontVariant} ${computed.fontWeight} ${computed.fontSize}/${computed.lineHeight} ${computed.fontFamily}`;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    context.font = fontString;

    let maxLabelWidth = 0;
    for (const item of this.list) {
      const width = context.measureText(item.label ?? '').width;
      if (width > maxLabelWidth) maxLabelWidth = width;
    }

    const ITEM_PADDING_X = 24;
    const SCROLLBAR = 8;
    const SAFETY = 8;

    // Ancho mínimo: el tamaño del input
    const inputEl = this.elementRef.nativeElement.querySelector('.p-autocomplete-input') as HTMLElement;
    const inputWidth = inputEl?.offsetWidth || 0;

    const finalWidth = Math.min(
      Math.max(inputWidth, Math.ceil(maxLabelWidth + ITEM_PADDING_X + SCROLLBAR + SAFETY)),
      Math.floor(window.innerWidth * 0.4)
    );

    this.overlayWidth = `${finalWidth}px`;
  }

  filterItems(event: AutoCompleteCompleteEvent) {
    if (this.disabledList) {
      this.filteredList = [];
      return;
    }

    const query = event.query.toLowerCase();
    this.filteredList = this.list.filter(item => item.label.toLowerCase().includes(query));
  }

  manualChange(event: Event) {
    if (this.disabledList) return;

    const query = (event.target as HTMLInputElement)?.value.toLowerCase();
    const value = this.list.find(x => x.label.toLowerCase() === query && !x.disabled);
    if (value && this.internalValue != value.value) {
      this.internalValue = value.value;
      this.onChange(value.value);
      this.onChangeSelect.emit(this.internalValue);
    }
  }

  emitExtraButtonClick(): void {
    this.clickExtraButton.emit();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  selectAllText(event: Event) {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  updateSelectedValue(): void {
    if (this.list?.length) {
      this.selectedValue = this.list.find(item => item.value === this.internalValue) || null;
    } else {
      this.selectedValue = null;
    }
  }

  writeValue(value: any): void {
    this.internalValue = value;
    this.selectedValue = this.list.find(item => item.value === value) || null;
  }

}
